import { IComboBox, IComboBoxOption } from '@fluentui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Granularity } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { ModelFilterOption, ModelFiltersResult } from '../types';

/**
 * Custom hook to manage model filter state (date-range-scoped)
 *
 * Manages the selection state for model filters. Filter options are provided
 * from external source which extracts them from already-fetched token usage data.
 *
 * The hook maintains two states:
 * - pendingModels: UI state that updates immediately on selection
 * - appliedModels: Committed state that triggers data fetches
 *
 * IMPORTANT: When date range changes, both pending and applied filters are automatically reset.
 * This is because model filters are scoped to the current date range.
 *
 * @param modelOptions Filter options extracted from model usage data
 * @param startTime Start of the current time range (for change detection)
 * @param endTime End of the current time range (for change detection)
 * @param granularity Granularity of the current time range (for change detection)
 * @returns Object containing model options, pending/applied models, handlers, and computed states
 */
export function useModelFilters(
  modelOptions: ModelFilterOption[],
  startTime: Date,
  endTime: Date,
  granularity: Granularity
): ModelFiltersResult {
  // Pending state: immediate UI updates, no fetch triggered
  const [pendingModels, setPendingModels] = useState<string[]>([]);

  // Applied state: committed filters that trigger data fetches
  const [appliedModels, setAppliedModels] = useState<string[]>([]);

  // Track previous date range to detect changes
  const prevDateRangeRef = useRef<string>('');
  const currentDateRangeKey = `${startTime.toISOString()}-${endTime.toISOString()}-${granularity}`;

  // Auto-reset filters when date range changes
  useEffect(() => {
    if (prevDateRangeRef.current !== '' && prevDateRangeRef.current !== currentDateRangeKey) {
      setPendingModels([]);
      setAppliedModels([]);
    }
    prevDateRangeRef.current = currentDateRangeKey;
  }, [currentDateRangeKey]);

  // Handle model filter changes (updates pending state only, no fetch)
  const handleChange = useCallback(
    (_event: React.FormEvent<IComboBox>, option?: IComboBoxOption) => {
      if (!option) return;

      setPendingModels((prev) => {
        const updatedModels = option.selected
          ? [...prev, option.key as string]
          : prev.filter((model) => model !== option.key);

        return updatedModels;
      });
    },
    [] // No dependencies - stable callback reference
  );

  // Apply handler: commits pending changes to applied state (triggers fetch)
  const handleApply = useCallback(() => {
    setAppliedModels(pendingModels);
  }, [pendingModels]);

  // Reset handler: implements two scenarios
  // 1. If no applied filters: clear pending only (no fetch)
  // 2. If applied filters exist: clear both pending and applied (triggers fetch)
  const handleReset = useCallback(() => {
    setPendingModels([]);

    // Only clear applied filters if there were any (triggers fetch only if needed)
    if (appliedModels.length > 0) {
      setAppliedModels([]);
    }
  }, [appliedModels]);

  // Computed values for button states
  const hasChanges = JSON.stringify([...pendingModels].sort()) !== JSON.stringify([...appliedModels].sort());
  const hasFiltersToReset = pendingModels.length > 0 || appliedModels.length > 0;

  return {
    modelOptions,
    selectedModels: pendingModels,
    appliedModels,
    handleChange,
    handleApply,
    handleReset,
    hasChanges,
    hasFiltersToReset,
  };
}
